import * as React from 'react';
import {useContext, useState} from 'react';
import {useTranslation} from 'react-i18next'
import * as Yup from 'yup';
import {
    ID_FILTER_DEALERS,
    ID_FILTER_LEGAL_ENTITIES,
    ID_FILTER_REGISTRY_CARD_NUMBER,
    ID_FILTER_REGISTRY_CARD_STATUS,
    ID_FILTER_REGISTRY_CARD_USE,
    ID_FILTER_REGISTRY_DUE_DATE_FROM,
    ID_FILTER_REGISTRY_DUE_DATE_TO,
    ID_FILTER_REGISTRY_ID_MS_ASSIGNEE,
    ID_FILTER_REGISTRY_MS_ASSIGNEE,
    ID_FILTER_REGISTRY_PROVIDER
} from "../constants/FuelFilters";
import {addFilter, getFilterValue} from "../../../utils/FilterUtils";
import {useRecoilValue, useSetRecoilState} from 'recoil';
import {STATE_REGISTRY_FUEL_ACTIVE_FILTERS} from '../constants/FuelCardRecoil';
import AdvancedFilter from "../../../component/AdvancedFilter";
import {Checkbox, Chip, ListItemText, MenuItem, TextField, Tooltip} from "@mui/material";
import {asEndOfDay, asStartOfDay} from "../../../utils/FinanceDateUtils";
import {
    ACTIVE,
    BLOCKED,
    CAR_SERVICE,
    ENI,
    ESSO,
    EXPIRED,
    fcReportFilterdefaultValues,
    IP,
    NEW_CAR,
    PROMISCUOUS,
    Q8,
    SUSPENDED
} from "../constants/FuelsConstants";
import {DatePicker} from "@mui/x-date-pickers";
import {useRegistryFuelCardStatuses} from "../constants/useRegistryFuelCardStatuses";
import {GlobalContext} from '../../../../../util/state/GlobalContextProvider';

function RegistryFuelCardsFilter() {
    const {t} = useTranslation('finance', {keyPrefix: 'registryFuelCard'});
        const { t: tg } = useTranslation("translation");
    const setFilterDefs = useSetRecoilState(STATE_REGISTRY_FUEL_ACTIVE_FILTERS);
    const filterDefs = useRecoilValue(STATE_REGISTRY_FUEL_ACTIVE_FILTERS);
    const {getRegistryFuelCardStatusColor, getRegistryFuelCardStatuses} = useRegistryFuelCardStatuses();

    // Use Context
    const {
        globalData: { user },
    } = useContext(GlobalContext);

    // Setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: any[] = user.legalEntities
    ?.filter((x: any) => x.name.toLowerCase() !== "all")
    .map((x: any) => ({
        value: x.name,
        label: tg(`legalEntities.${x.name}`),
    }));

    const dealerOptions: any[] = user.dealers
    ?.filter((x: any) => x.name.toLowerCase() !== "all")
    .map((x: any) => ({
        value: x.name,
        label: tg(`dealers.${x.name}`),
    }));
    const [dealers, setDealers] = useState<any[]>(dealerOptions);
    
    const schema = Yup.object().shape({
        provider: Yup.array().nullable(),
        cardNumber: Yup.string().nullable(),
        cardUse: Yup.array().nullable(),
        state: Yup.array().nullable(),
        msId: Yup.string().nullable(),
        msAssignee: Yup.string().nullable(),
        dueDateFrom: Yup.date().nullable().test("dueDateFromTest", t('filters.errors.invalidDate'),  (value, context) => {
            return !value || !context.parent.dueDateTo || (value <= context.parent.dueDateTo);
        }).typeError(t('filters.errors.invalidDate')),
        dueDateTo: Yup.date().nullable().test("dueDateToTest", t('filters.errors.invalidDate'), (value, context) => {
            return !value || !context.parent.dueDateFrom || (value >= context.parent.dueDateFrom);
        }).typeError(t('filters.errors.invalidDate')),
        dealers: Yup.array(),
        legalEntities: Yup.array().min(1, tg('validation.selectAtLeastOne')),
    });


    const defaultValues = {
        provider: getFilterValue(ID_FILTER_REGISTRY_PROVIDER, filterDefs) || [] as Array<string>,
        cardNumber: getFilterValue(ID_FILTER_REGISTRY_CARD_NUMBER, filterDefs) || '',
        cardUse: getFilterValue(ID_FILTER_REGISTRY_CARD_USE, filterDefs) || [] as Array<string>,
        state: getFilterValue(ID_FILTER_REGISTRY_CARD_STATUS, filterDefs) || [] as Array<string>,
        msId: getFilterValue(ID_FILTER_REGISTRY_ID_MS_ASSIGNEE, filterDefs) || '',
        msAssignee: getFilterValue(ID_FILTER_REGISTRY_MS_ASSIGNEE, filterDefs) || '',
        dueDateFrom: getFilterValue(ID_FILTER_REGISTRY_DUE_DATE_FROM, filterDefs) || null,
        dueDateTo: getFilterValue(ID_FILTER_REGISTRY_DUE_DATE_TO, filterDefs) || null,
        legalEntities: getFilterValue(ID_FILTER_LEGAL_ENTITIES, filterDefs) || legalEntityOptions.map((x: any) => x.value),
        dealers: getFilterValue(ID_FILTER_DEALERS, filterDefs) || []
    }


    const renderProviderField =  (field: any) => (
        <TextField
            style={{width: "100%"}}
            select
            SelectProps={{
                multiple: true,
                renderValue: (selected: any) => selected.map((s: any) => providers.find(ss => ss.value === s)!.label).join(', ')
            }}
            label={t('filters.' + ID_FILTER_REGISTRY_PROVIDER)}
            value={field.value}
            onChange={field.onChange}
        >
            {providers.map((provider) => (
                <MenuItem key={provider.value} value={provider.value}>
                    <Checkbox checked={field.value.indexOf(provider.value) > -1}/>
                    <ListItemText primary={provider.label}/>
                </MenuItem>
            ))}
        </TextField>
    )

    const renderCardNumberField = (field: any) => (
        <TextField
            fullWidth
            value={field.value}
            onChange={field.onChange}
            label={t('filters.cardNumber')}
        />
    )

    const renderCardUseField =  (field: any) => (
        <TextField
            style={{width: "100%"}}
            select
            SelectProps={{
                multiple: true,
                renderValue: (selected: any) => selected.map((s: any) => cardUse.find(ss => ss.value === s)!.label).join(', ')
            }}
            label={t('filters.' + ID_FILTER_REGISTRY_CARD_USE)}
            value={field.value}
            onChange={field.onChange}
        >
            {cardUse.map((card) => (
                <MenuItem key={card.value} value={card.value}>
                    <Checkbox checked={field.value.indexOf(card.value) > -1}/>
                    <ListItemText primary={card.label}/>
                </MenuItem>
            ))}
        </TextField>
    )

    const renderStateField =  (field: any) => (
        <TextField
            style={{width: "100%"}}
            select
            SelectProps={{
                multiple: true,
                renderValue: (selected: any) => selected.map((s: any) => statuses.find(ss => ss.value === s)!.label).join(', ')
            }}
            label={t('filters.' + ID_FILTER_REGISTRY_CARD_STATUS)}
            value={field.value}
            onChange={field.onChange}
        >
            {statuses.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                    <Tooltip title="">
                        <>
                            <Checkbox checked={field.value.indexOf(status.value) > -1}/>
                            <Chip label={getRegistryFuelCardStatuses(status.value)}
                                  color={getRegistryFuelCardStatusColor(status.value)}/>
                        </>
                    </Tooltip>
                </MenuItem>
            ))}
        </TextField>
    )

    const renderMsAssigneeField = (field: any) => (
        <TextField
            fullWidth
            value={field.value}
            onChange={field.onChange}
            label={t('filters.msAssignee')}
        />
    )

    const renderIdMsAssigneeField = (field: any) => (
        <TextField
            fullWidth
            value={field.value}
            onChange={field.onChange}
            label={t('filters.idMsAssignee')}
        />
    )

    const renderDueDateFromField = (field: any, error: any, state: any, values: any) => (
        <DatePicker
            views={['day']}
            label={t('filters.dueDateFrom')}
            value={field.value}
            maxDate={values['dueDateTo'] !== null ? values['dueDateTo'] : null}
            onChange={field.onChange}
            renderInput={(params) =>
                <TextField {...params} style={{width: "100%"}}
                           error={error !== undefined}
                           helperText={error ? error.message : ""}
                           id={"dueDateFrom"}
                />}
            inputFormat={"L"}
        />
    )
    const renderDueDateToField = (field: any, error: any, state: any, values: any) => (
        <DatePicker
            views={['day']}
            label={t('filters.dueDateTo')}
            value={field.value}
            minDate={values['dueDateFrom'] !== null ? values['dueDateFrom'] : null}
            onChange={field.onChange}
            renderInput={(params) =>
                <TextField {...params} style={{width: "100%"}}
                           error={error !== undefined}
                           helperText={error ? error.message : ""}
                           id={"dueDateTo"}
                />}
            inputFormat={"L"}
        />
    )
    const renderMultiSelect = (label: string, list: any[], name: string, isHidden: boolean, onChange?: any) => {
            return (field: any, error: any, formState: any, watchAll: any, setValue: any) => (
                <TextField
                name={name}
                fullWidth
                select
                hidden={isHidden}
                disabled={list.length==0}
                SelectProps={{
                    multiple: true,
                    renderValue: (selected: any) =>
                    selected
                        .map((s: any) => list.find((ss) => ss.value === s)!.label)
                        .join(", "),
                }}
                label={label}
                value={field.value}
                onChange={(event) => {
                    if (onChange) {
                    onChange(event.target.value, setValue);
                    }
                    field.onChange(event);
                }}
                error={error !== undefined}
                helperText={error ? error.message : ""}
                >
                {list.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                    <Checkbox checked={field.value?.indexOf(item.value) > -1} />
                    <ListItemText primary={item.label} />
                    </MenuItem>
                ))}
                </TextField>
            );
    };
    const mainFields = {
        cardNumber: renderCardNumberField,
        provider: renderProviderField,
        state: renderStateField,
    };

    const fields = {
        msId: renderIdMsAssigneeField,
        msAssignee: renderMsAssigneeField,
        dueDateFrom: renderDueDateFromField,
        dueDateTo: renderDueDateToField,
        cardUse: renderCardUseField,
        legalEntities: renderMultiSelect(
            tg("shared.legalEntities"),
            legalEntityOptions,
            "legalEntities",
            legalEntityOptions.length <= 1,
            (value: any, setValue: any) => {
              setValue('dealers', []);
              let dealers = user?.dealers
                ?.filter(
                  (x: any) =>
                    value?.length > 0 &&
                    x.legalEntities.some((e: any) => value.includes(e.name)) &&
                    x.name.toLowerCase() !== "all"
                )
                .map((x: any) => {
                  return {
                    label: tg(`dealers.${x.name}`),
                    value: x.name,
                  };
                });
              setDealers(dealers);
            }
          ),
        dealers: renderMultiSelect(tg("shared.dealers"), dealers, "dealers", dealerOptions.length <= 1),
    };

    const providers = [
        { value: ENI, label: t('filters.providers.' + ENI)},
        { value: IP,  label: t('filters.providers.' + IP)},
        { value: Q8,  label: t('filters.providers.' + Q8)},
        { value: ESSO,  label: t('filters.providers.' + ESSO)}
    ];

    const cardUse = [
        { value: NEW_CAR, label: t('filters.cardUseList.' + NEW_CAR)},
        { value: PROMISCUOUS,  label: t('filters.cardUseList.' + PROMISCUOUS)},
        { value: CAR_SERVICE,  label: t('filters.cardUseList.' + CAR_SERVICE)}
    ];

    const statuses = [
        { value: ACTIVE, label: t('filters.statuses.' + ACTIVE)},
        { value: EXPIRED,  label: t('filters.statuses.' + EXPIRED) },
        { value: BLOCKED,  label: t('filters.statuses.' + BLOCKED) },
        { value: SUSPENDED,  label: t('filters.statuses.' + SUSPENDED) }
    ];


    const submit = (data: any) => {
        addFilter(setFilterDefs, ID_FILTER_REGISTRY_PROVIDER, data.provider);
        addFilter(setFilterDefs, ID_FILTER_REGISTRY_CARD_NUMBER, data.cardNumber);
        addFilter(setFilterDefs, ID_FILTER_REGISTRY_CARD_USE, data.cardUse);
        addFilter(setFilterDefs, ID_FILTER_REGISTRY_CARD_STATUS, data.state);
        addFilter(setFilterDefs, ID_FILTER_REGISTRY_ID_MS_ASSIGNEE, data.msId);
        addFilter(setFilterDefs, ID_FILTER_REGISTRY_MS_ASSIGNEE, data.msAssignee);
        addFilter(setFilterDefs, ID_FILTER_REGISTRY_DUE_DATE_FROM, asStartOfDay(data.dueDateFrom));
        addFilter(setFilterDefs, ID_FILTER_REGISTRY_DUE_DATE_TO, asEndOfDay(data.dueDateTo));
        addFilter(setFilterDefs, ID_FILTER_LEGAL_ENTITIES, data.legalEntities);
        addFilter(setFilterDefs, ID_FILTER_DEALERS, data.dealers);
    }


    const reset = () => {
        setFilterDefs([]);
        setDealers(dealerOptions);
    }

    return (
        <AdvancedFilter schema={schema}
                        defaultValues={defaultValues}
                        mainFields={mainFields}
                        fields={fields}
                        onSubmit={submit}
                        onReset={reset}
                        customDefaultValues={fcReportFilterdefaultValues}
        />
    )
}

export default RegistryFuelCardsFilter;